/**
 * RouteCorridorCard - Mobile/tablet card for a route corridor row
 */

import React from "react";
import {
  Card,
  Typography,
  Button,
  Box,
  Chip,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { Popconfirm } from "antd";
import { formatDistanceToNow } from "date-fns";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 10,
  boxShadow: "none",
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  height: "100%",
}));

const CardBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.75),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.25),
  height: "100%",
}));

const HeaderRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 8,
});

const NameText = styled(Typography)({
  fontWeight: 700,
  fontSize: "1.05rem",
  letterSpacing: "0.02em",
  wordBreak: "break-word",
});

const DescriptionText = styled(Typography)({
  fontSize: "0.875rem",
  color: "rgba(0, 0, 0, 0.6)",
  lineHeight: 1.4,
});

const ProvinceRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
});

const MetaText = styled(Typography)({
  fontSize: "0.75rem",
  color: "rgba(0, 0, 0, 0.55)",
});

const ActionsRow = styled(Box)({
  display: "flex",
  gap: 8,
  marginTop: "auto",
  paddingTop: 4,
});

/**
 * @param {Object} props
 * @param {Object} props.routeCorridor
 * @param {Function} props.onEdit
 * @param {Function} props.onDelete
 */
const RouteCorridorCard = ({ routeCorridor, onEdit, onDelete }) => {
  if (!routeCorridor) return null;

  const {
    name,
    description,
    provinces = [],
    active,
    createdAt,
  } = routeCorridor;

  const createdLabel = createdAt
    ? formatDistanceToNow(new Date(createdAt), { addSuffix: true })
    : null;

  return (
    <StyledCard>
      <CardBody>
        <HeaderRow>
          <NameText variant="body1" component="div">
            {name}
          </NameText>
          <Chip
            size="small"
            label={active ? "Active" : "Inactive"}
            color={active ? "success" : "default"}
            sx={{ fontWeight: 600, flexShrink: 0 }}
          />
        </HeaderRow>

        {description ? (
          <DescriptionText variant="body2">{description}</DescriptionText>
        ) : (
          <DescriptionText variant="body2" sx={{ fontStyle: "italic" }}>
            No description
          </DescriptionText>
        )}

        <ProvinceRow>
          {provinces.length > 0 ? (
            provinces.map((province) => (
              <Chip
                key={province}
                label={province}
                size="small"
                variant="outlined"
                color="primary"
              />
            ))
          ) : (
            <MetaText variant="caption">No provinces</MetaText>
          )}
        </ProvinceRow>

        {createdLabel && (
          <MetaText variant="caption">Created {createdLabel}</MetaText>
        )}

        <ActionsRow>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<Edit />}
            onClick={() => onEdit?.(routeCorridor)}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 600,
              minHeight: 44,
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Route Corridor"
            description={`Delete "${name}"? This cannot be undone.`}
            onConfirm={() => onDelete?.(routeCorridor)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Button
              variant="outlined"
              color="error"
              size="medium"
              startIcon={<Delete />}
              sx={{
                flex: 1,
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
              }}
            >
              Delete
            </Button>
          </Popconfirm>
        </ActionsRow>
      </CardBody>
    </StyledCard>
  );
};

export default RouteCorridorCard;
